import React, { useState, useRef, useEffect, useCallback } from "react";
import { Autocomplete, TextField, CircularProgress, Chip } from "@mui/material";
import useApi from "../../hooks/useApi";
import {
  Save,
  PlusSquare,
  PackageSearch,
  ChevronDown,
  CheckCircle2,
  Hash,
  Tag,
  MapPin,
  PackageCheck,
} from "lucide-react";
import Modal from "../common/CommonModal";
import CreditCustomerList from "../common/CreditCustomerList";
import TransferOrderModal from "../salesInvoice/TransferOrderModal";
import { DOCUMENT_TYPES, getDocumentConfig } from "../config/DocumentConfig";
import { useNavigate, useLocation } from "react-router-dom";

const GitHome = ({ documentType = DOCUMENT_TYPES.GOODS_IN_TRANSIT }) => {
  const COMPANY_CODE = localStorage?.getItem("companyCode") || "DOODDEMO";
  const DEPARTMENT =
    localStorage.getItem("currentSelectedDepartment") || "MAIN";
  const API_PATHS = localStorage.getItem("apiPaths")
    ? JSON.parse(localStorage.getItem("apiPaths"))
    : "-";
  const BASE_URL = API_PATHS[0]?.API_CONFIG || "https://cubixweberp.com:213";
  const config = getDocumentConfig(documentType);
  const [documentNo, setDocumentNo] = useState("");
  const [orderType, setOrderType] = useState("Import");
  const [filterActive, setFilterActive] = useState(false);

  const [supplier, setSupplier] = useState(null);
  const [supplierInput, setSupplierInput] = useState("");
  const [supplierOptions, setSupplierOptions] = useState([]);
  const [supplierLoading, setSupplierLoading] = useState(false);
  const [modalState, setModalState] = useState({
    isOpen: false,
    type: "", // 'add', 'cr-list'
    customer: null,
  });
  const navigate = useNavigate();
  const location = useLocation();
  const mode = location.state?.mode || "NEW";
  const docNoFromState = location.state?.docNo || "";
  const isEditMode = mode === "edit";
  const isViewMode = mode === "view";

  const [modalSearchTerm, setModalSearchTerm] = useState("");
  const [currencies, setCurrencies] = useState([]);
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [modalData, setModalData] = useState([]);
  const [loadingModalData, setLoadingModalData] = useState(false);

  const autocompleteRef = useRef();
  const { request } = useApi();

  const [documentData, setDocumentData] = useState({
    CmpCode: COMPANY_CODE,
    mode: "NEW",
    po_no: documentNo,
    po_date: new Date().toISOString().slice(0, 10),
    cust_acc: supplier?.ACCOUNT || "",
    jv_num: "00",
    comments: "",
    sale_man: localStorage.getItem("currentUserName"),
    inv_no: 0,
    po_status: "N",
    area_code: "",
    po_refmain: "",
    po_doc: documentNo,
    fc: "AED",
    po_amount: 0,
    po_fcamt: 0,
    po_fcrate: 1,
    pi_amount: 0,
    pi_fcamt: 0,
    po_disc: 0,
    due_date: "",
    po_fdisc: 0,
    accdesc: supplier?.DESCRIPTION || "",
    delivery: "",
    shipment: "",
    payment: "",
    consignee: "",
    notify: "",
    head1: "",
    foot1: "",
    status1: "",
    account: "",
    from: "",
    to: "",
    partial: "",
    others: "",
    validity: "",
    tranship: "",
    lpo_no: "",
    deptno: DEPARTMENT,
    CCNO: "",
    job_code: "",
  });
  const fetchGitDetails = useCallback(async () => {
    try {
      const url = `${BASE_URL}/api/DocumentEditDetails/${COMPANY_CODE}/GIT/${docNoFromState}/${DEPARTMENT}`;
      const response = await fetch(url);
      const data = await response.json();
      if (data && data.length > 0) {
        const firstRow = data[0];
        setDocumentNo(firstRow.po_no);
        setSupplier({
          ACCOUNT: firstRow.cust_acc,
          DESCRIPTION: firstRow.accdesc,
        });

        setDocumentData({
          CmpCode: COMPANY_CODE,
          mode: "edit",
          po_no: firstRow.po_no,
          po_date: firstRow.po_date?.split("T")[0],
          cust_acc: firstRow.cust_acc,
          jv_num: firstRow.jv_num,
          comments: firstRow.comments,
          sale_man: firstRow.sale_man,
          inv_no: firstRow.inv_no,
          po_status: firstRow.po_status,
          area_code: firstRow.area_code,
          po_refmain: firstRow.po_ref || "",
          po_doc: firstRow.po_doc,
          fc: firstRow.fc,
          po_amount: firstRow.po_amount,
          po_fcamt: firstRow.po_fcamt,
          po_fcrate: firstRow.po_fcrate,
          pi_amount: firstRow.pi_amount,
          pi_fcamt: firstRow.pi_fcamt,
          po_disc: firstRow.po_disc,
          due_date: firstRow.due_date?.split("T")[0],
          po_fdisc: firstRow.po_fdisc,
          accdesc: firstRow.accdesc,
          delivery: firstRow.delivery,
          shipment: firstRow.shipment,
          payment: firstRow.payment,
          consignee: firstRow.consignee,
          notify: firstRow.notify,
          head1: firstRow.head1,
          foot1: firstRow.foot1,
          status1: firstRow.status1,
          account: firstRow.account,
          from: firstRow.from,
          to: firstRow.to,
          partial: firstRow.partial,
          others: firstRow.others,
          validity: firstRow.validity,
          tranship: firstRow.tranship,
          lpo_no: firstRow.lpo_no,
          deptno: firstRow.deptno,
          CCNO: firstRow.CCNO,
          job_code: firstRow.job_code,
        });

        // if (firstRow.po_status === "Import") setOrderType("Import");
        // else setOrderType("Local");

        const mappedItems = data.map((row, idx) => ({
          id: idx + 1,
          poNo: row.po_ref || row.Expr1,
          code: row.po_icode,
          desc: row.idesc,
          loc: row.po_locn,
          unit: row.unit?.trim(),
          pendQty: row.shp2_qty, 
          shipQty: row.po_qty,
          cost: row.po_cost,
          cntrl: row.souid || "",
        }));
        setItems(mappedItems);
      }
    } catch (err) {
      console.error("Error fetching GIT details:", err);
    }
  }, [BASE_URL, COMPANY_CODE, DEPARTMENT, docNoFromState]);

  useEffect(() => {
    async function getDocNo() {
      try {
        const response = await fetch(
          `${BASE_URL}/api/CRMVoucherSettings/${COMPANY_CODE}/VOUCHERNUMBERS/${DEPARTMENT}/GET/GI/-`,
        );
        const data = await response.json();
        setDocumentNo(data[0].NEXT_NO || "");
      } catch (err) {
        setDocumentNo("");
      }
    }

    if (isEditMode || isViewMode) {
      fetchGitDetails();
    } else {
      getDocNo();
    }
  }, [isEditMode, isViewMode, fetchGitDetails]);

  useEffect(() => {
    const fetchCurrencies = async () => {
      try {
        const url = `${BASE_URL}/api/cpayscount/${COMPANY_CODE}/currency/-/-`;
        const response = await fetch(url);
        const data = await response.json();
        setCurrencies(data || []);
      } catch (error) {
        console.error("Error fetching currencies:", error);
      }
    };
    fetchCurrencies();
  }, [BASE_URL, COMPANY_CODE]);

  const fetchSuppliers = useCallback(
    async (query = "-") => {
      setSupplierLoading(true);
      try {
        const COMPANY_CODE = localStorage?.getItem("companyCode") || "DOODDEMO";
        const DEPARTMENT =
          localStorage.getItem("currentSelectedDepartment") || "MAIN";
        const API_PATHS = localStorage.getItem("apiPaths")
          ? JSON.parse(localStorage.getItem("apiPaths"))
          : "-";
        const BASE_URL =
          API_PATHS[0]?.API_CONFIG || "https://cubixweberp.com:213";

        const url = `${BASE_URL}/api/CRMAccountList/${COMPANY_CODE}/ACCOUNTLIST/${DEPARTMENT}/SUPPLIERS/-/${encodeURIComponent(query?.trim())}`;

        const response = await request({ url, method: "GET", timeout: 1000 });
        setSupplierOptions(response || []);
      } catch (err) {
        console.error("Supplier fetch error:", err);
        setSupplierOptions([]);
      } finally {
        setSupplierLoading(false);
      }
    },
    [request],
  );
  useEffect(() => {
    if (supplier) {
      setDocumentData((prev) => ({
        ...prev,
        cust_acc: supplier.ACCOUNT || supplier.account || "",
        account: supplier.ACCOUNT || supplier.account || "",
        accdesc: supplier.DESCRIPTION || supplier.name || "",
      }));
    } else {
      setDocumentData((prev) => ({
        ...prev,
        cust_acc: "",
        account: "",
        accdesc: "",
      }));
    }
  }, [supplier]);

  useEffect(() => {
    if (supplierInput && supplierInput.length > 0) {
      const timeout = setTimeout(() => {
        fetchSuppliers(supplierInput);
      }, 300);
      return () => clearTimeout(timeout);
    } else {
      setSupplierOptions([]);
    }
  }, [supplierInput]);

  const [items, setItems] = useState([]);
  const tableGrid =
    "40px 120px 1fr 70px 70px 110px 110px 110px 120px 120px 100px 100px";
  const handleQtyChange = (id, value) => {
    const numValue = parseFloat(value) || 0;
    setItems((prev) =>
      prev.map((item) =>
        item.id === id
          ? { ...item, shipQty: Math.min(numValue, item.pendQty) }
          : item,
      ),
    );
  };

  // Filter Logic
  const activeItems = items.filter((i) => i.shipQty > 0);
  const displayedItems = filterActive ? activeItems : items;

  // Financial Calculations
  const totalQty = activeItems.reduce((acc, curr) => acc + curr.shipQty, 0);
  const netAmount = activeItems.reduce(
    (acc, curr) => acc + curr.shipQty * curr.cost,
    0,
  );

  // Number Formatter
  const fmt = (val) =>
    new Intl.NumberFormat("en-AE", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(val);

  const closeModal = () => {
    setModalState({
      isOpen: false,
      type: "",
      customer: null,
    });
  };
  const getOrderId = (order = {}) => {
    return (
      order.QUOTNO ||
      order.ORDNO ||
      order.SONO ||
      order.PONO ||
      order.DONO ||
      order.ISSUE_NO ||
      order.lead_id ||
      order.id || // fallback if given
      null
    );
  };
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedOrders(modalData.map(getOrderId).filter(Boolean));
    } else {
      setSelectedOrders([]);
    }
  };

  const handleSelectOrder = (orderId) => {
    if (!orderId) return; // skip bad rows
    console.log("Toggling order ID:", orderId);
    setSelectedOrders((prev) =>
      prev.includes(orderId)
        ? prev.filter((id) => id !== orderId)
        : [...prev, orderId],
    );
  };

  const fetchModalData = async () => {
    const query = modalSearchTerm.trim() === "" ? "-" : modalSearchTerm;
    const url = `${BASE_URL}/api/CRMDocTransfer/${COMPANY_CODE}/LIST/PORD/-/${
      supplier?.account || supplier?.ACCOUNT || "-"
    }/${DEPARTMENT}/${query}/-`;
    try {
      const response = await request({
        url: url,
        method: "GET",
        timeout: 10000,
      });
      console.log("Response", response);
      setModalData(response || []);
    } catch (err) {
      console.error("Error loading data:", err);
      setModalData([]);
    }
  };
  const handleTransfer = async (docType, selectedRows) => {
    console.log(
      "Selected rows for Transfer:",
      selectedRows,
      "Doc Type:",
      docType,
    );
    setModalState((prev) => ({ ...prev, isOpen: false }));
    if (!selectedRows || selectedRows.length === 0) {
      alert("Please select at least one PO to import.");
      return;
    }
    // Compose docNos string
    const docNos = selectedRows.map((row) => row.ORDNO).join(",");
    console.log("Composed docNos for API:", docNos);
    const masterUrl = `${BASE_URL}${config.apiEndpoints.transfer.master(
      COMPANY_CODE,
      DEPARTMENT,
      docNos,
      docType,
    )}`;
    const detailUrl = `${BASE_URL}${config.apiEndpoints.transfer.detail(
      COMPANY_CODE,
      DEPARTMENT,
      docNos,
      docType,
    )}`;
    try {
      const masterRes = await fetch(masterUrl);
      const masterData = await masterRes.json();
      console.log("[GitHome] Master Transfer Response:", masterData);
      if (masterData && masterData.length > 0) {
        const masterInfo = masterData[0];
        setDocumentData((prev) => ({
          ...prev,
          from: masterInfo.accdesc || "",
          account: masterInfo.cust_acc || "",
          accdesc: masterInfo.accdesc || "",
        }));
      }
      const detailRes = await fetch(detailUrl);
      const detailData = await detailRes.json();
      console.log("[GitHome] Detail Transfer Response:", detailData);
      if (detailData && detailData.length > 0) {
        console.log("Detail Data:", detailData);
        const importedItems = detailData.map((row, idx) => ({
          id: idx + 1,
          poNo: row.po_no || row.ORDNO || row.SONO,
          code: row.code || row.po_icode || row.so_icode || row.idesc,
          desc: row.idesc,
          loc: row.po_locn || row.locn,
          unit: row.unit?.trim(),
          pendQty: row.po_qty || row.so_qty || 0,
          shipQty: 0,
          cost: row.cost_unit || row.so_fccost || row.po_cost || 0,
          cntrl: row.souid || "",
        }));
        setItems(importedItems);
      }
    } catch (err) {
      console.error("[GitHome] Error fetching transfer data:", err);
    }
  };

  const handleSave = async () => {
    try {
      const payload = items
        .filter((item) => Number(item.shipQty) > 0)
        .map((item, idx) => ({
          ...documentData,
          Slno: idx + 1,
          code: item.code,
          description: item.desc,
          locn: item.loc,
          unit: item.unit,
          "Shipped Qty": item.shipQty,
          "Pend Qty": item.pendQty,
          "unit Cost": item.cost,
          Amount: item.shipQty * item.cost,
          po_ref: item.po_ref || item.poNo || "",
          x: item.x || "",
          cntrl: item.cntrl || "",
          frac: item.frac || 1.0,
          OEM: item.OEM || "",
          pono: item.pono || "0",
          ordQty: item.ordQty || item.pendQty || 0,
          rcvdQty: item.rcvdQty || 0,
          Adj: item.Adj || 0.0,
          TrDoc: item.TrDoc || "0",
          DocNo: item.DocNo || idx + 1,
          // Header fields (ensure mapping from UI or state)
          CmpCode: documentData.CmpCode,
          mode: documentData.mode,
          po_no: documentNo,
          po_date: documentData.po_date,
          cust_acc: documentData.account,
          jv_num: documentData.jv_num,
          comments: documentData.comments,
          sale_man: documentData.sale_man,
          inv_no: documentData.inv_no,
          po_status: documentData.po_status,
          area_code: documentData.area_code,
          po_refmain: documentData.po_refmain,
          po_doc: documentNo,
          fc: documentData.fc,
          po_amount: documentData.po_amount,
          po_fcamt: documentData.po_fcamt,
          po_fcrate: documentData.po_fcrate,
          pi_amount: documentData.pi_amount,
          pi_fcamt: documentData.pi_fcamt,
          po_disc: documentData.po_disc,
          due_date: documentData.due_date,
          po_fdisc: documentData.po_fdisc,
          accdesc: documentData.accdesc,
          delivery: documentData.delivery,
          shipment: documentData.shipment,
          payment: documentData.payment,
          consignee: documentData.consignee,
          notify: documentData.notify,
          head1: documentData.head1,
          foot1: documentData.foot1,
          status1: documentData.status1,
          account: documentData.account,
          from: documentData.from,
          to: documentData.to,
          partial: documentData.partial,
          others: documentData.others,
          validity: documentData.validity,
          tranship: documentData.tranship,
          lpo_no: documentData.lpo_no,
          deptno: documentData.deptno,
          CCNO: documentData.CCNO,
          job_code: documentData.job_code,
        }));
      const response = await fetch(`${BASE_URL}/api/GITCrm`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const result = await response.json();
      console.log(result);
      if (response.status === 200) {
        alert("Saved successfully!");
        navigate("/goods_in_transit_list");
      } else {
        alert("Save failed.");
      }
    } catch (err) {
      alert("Error saving document.");
    }
  };
  return (
    <div>
      <header className="bg-white rounded-2xl shadow-sm border border-slate-200/80 px-5 py-3.5 mb-4 flex justify-between items-center">
        <div className="flex items-center gap-3.5">
          {/* <div className="w-10 h-10 rounded-xl bg-indigo-600 flex items-center justify-center shadow-lg shadow-indigo-200">
            <Truck size={18} className="text-white" />
          </div> */}
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-base font-bold text-slate-900 tracking-tight">
                Goods In Transit
              </h1>
              <span className="mono text-[10px] bg-indigo-50 text-indigo-600 border border-indigo-100 px-1.5 py-0.5 rounded-md font-medium">
                {documentNo || "..."}
              </span>
              {isEditMode && (
                <span className="text-[10px] bg-amber-50 text-amber-600 border border-amber-100 px-1.5 py-0.5 rounded-md font-medium uppercase">
                  Edit Mode
                </span>
              )}
              {isViewMode && (
                <span className="text-[10px] bg-slate-100 text-slate-600 border border-slate-200 px-1.5 py-0.5 rounded-md font-medium uppercase">
                  View Mode
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2.5">
          <button
            className="flex items-center gap-2 bg-slate-50 border border-slate-200 text-slate-600 px-4 py-2 rounded-xl font-semibold text-xs hover:bg-slate-100 transition"
            onClick={() => {
              console.log("Import POs button clicked");
              setModalState({
                isOpen: true,
                type: "po-import",
                customer: null,
              });
            }}
            disabled={!supplier}
            title={!supplier ? "Select a supplier first" : "Import POs"}
          >
            <PlusSquare size={14} /> Import POs
          </button>

          <button
            onClick={handleSave}
            disabled={isViewMode}
            className={`${isViewMode ? "bg-slate-300 cursor-not-allowed" : "bg-indigo-600 hover:bg-indigo-700"} text-white px-5 py-2 rounded-xl font-semibold text-xs shadow-lg shadow-indigo-200/60 transition flex items-center gap-2`}
          >
            <Save size={14} /> {isEditMode ? "Update Record" : "Save Record"}
          </button>
        </div>
      </header>

      {/* ── META GRID ── */}
      <div className="grid grid-cols-12 gap-4 mb-4">
        {/* Basic Info */}
        <div className="col-span-3 bg-white rounded-2xl p-4 border border-slate-200/80 shadow-sm">
          <div className="space-y-3.5">
            <div>
              <label className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider block mb-1.5">
                Order Type
              </label>
              <div className="flex bg-slate-100 p-0.5 rounded-lg">
                {["Import", "Local"].map((t) => (
                  <button
                    key={t}
                    onClick={() => setOrderType(t)}
                    className={`flex-1 py-1.5 text-[10px] font-bold rounded-md uppercase transition-all ${orderType === t ? "bg-white shadow-sm text-indigo-600" : "text-slate-400 hover:text-slate-600"}`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider block mb-1">
                  A/C Number
                </label>
                <input
                  type="text"
                  value={documentData.cust_acc || ""}
                  readOnly
                  onChange={(e) =>
                    setDocumentData((prev) => ({
                      ...prev,
                      cust_acc: e.target.value,
                    }))
                  }
                  className="w-full text-xs font-semibold border-0 border-b border-slate-200 focus:border-indigo-400 outline-none py-1 bg-transparent transition-colors"
                />
              </div>
              <div>
                <label className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider block mb-1">
                  Entry Date
                </label>
                <input
                  type="date"
                  value={documentData.po_date || ""}
                  onChange={(e) =>
                    setDocumentData((prev) => ({
                      ...prev,
                      po_date: e.target.value,
                    }))
                  }
                  className="w-full text-xs border-0 border-b border-slate-200 outline-none py-1 bg-transparent"
                />
              </div>
            </div>
            <div>
              <label className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider block mb-1">
                Supplier TRN
              </label>
              <input
                type="text"
                value={supplier?.TRN ?? ""}
                placeholder="Auto-populated on supplier select"
                className="w-full text-xs mono border-0 border-b border-dashed border-slate-200 outline-none py-1 bg-slate-50/50 text-slate-500"
              />
            </div>
          </div>
        </div>

        {/* Logistics — includes supplier chooser */}
        <div className="col-span-6 bg-white rounded-2xl p-4 border border-slate-200/80 shadow-sm">
          {/* <div className="flex items-center gap-2 mb-3">
            <div className="w-5 h-5 rounded-md bg-slate-100 flex items-center justify-center">
              <Globe size={11} className="text-slate-500" />
            </div>
            <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
              Logistics & Shipping
            </span>
          </div> */}

          {/* Supplier chooser (MUI Autocomplete) */}
          <div className="mb-4">
            {/* <label className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider block mb-1.5">
              Supplier
            </label> */}
            <Autocomplete
              ref={autocompleteRef}
              value={supplier}
              onChange={(event, newValue) => {
                setSupplier(newValue);
              }}
              inputValue={supplierInput}
              onInputChange={(event, newInputValue) => {
                setSupplierInput(newInputValue);
              }}
              options={supplierOptions}
              getOptionLabel={(option) =>
                option.DESCRIPTION || option.name || ""
              }
              loading={supplierLoading}
              filterOptions={(x) => x} // Disable built-in filtering
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select a supplier"
                  variant="standard"
                  size="small"
                  placeholder="Search supplier..."
                  onKeyDown={(e) => {
                    if (e.key === "F2") {
                      e.preventDefault();

                      if (autocompleteRef.current) {
                        autocompleteRef.current.blur();
                      }
                      setModalState({
                        isOpen: true,
                        type: "cr-list",
                        customer: null,
                      });
                    }
                  }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {supplierLoading && (
                          <CircularProgress color="inherit" size={20} />
                        )}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => (
                <li {...props}>
                  <div className="flex items-center justify-between w-full">
                    <span>{option.DESCRIPTION || option.name}</span>
                    {option.ACCOUNT && (
                      <Chip
                        label={option.ACCOUNT}
                        size="small"
                        color={"primary"}
                        sx={{ height: 20, fontSize: 10 }}
                      />
                    )}
                  </div>
                </li>
              )}
              noOptionsText={
                supplierInput.length === 0
                  ? "Start typing to search..."
                  : "No suppliers found"
              }
            />
          </div>

          <div className="grid grid-cols-4 gap-x-5 gap-y-3.5">
            <div>
              <label className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider block mb-1">
                Order Ref
              </label>
              <input
                type="text"
                value={documentData.po_refmain || ""}
                onChange={(e) =>
                  setDocumentData((prev) => ({
                    ...prev,
                    po_refmain: e.target.value,
                  }))
                }
                className="w-full text-xs border-0 border-b border-slate-200 focus:border-indigo-400 outline-none py-1 bg-transparent transition-colors"
              />
            </div>
            <div>
              <label className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider block mb-1">
                Attention
              </label>
              <input
                type="text"
                value={documentData.consignee || ""}
                onChange={(e) =>
                  setDocumentData((prev) => ({
                    ...prev,
                    consignee: e.target.value,
                  }))
                }
                className="w-full text-xs border-0 border-b border-slate-200 focus:border-indigo-400 outline-none py-1 bg-transparent transition-colors"
              />
            </div>
            <div>
              <label className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider block mb-1">
                Terms
              </label>
              <input
                type="text"
                value={documentData.payment || ""}
                onChange={(e) =>
                  setDocumentData((prev) => ({
                    ...prev,
                    payment: e.target.value,
                  }))
                }
                className="w-full text-xs border-0 border-b border-slate-200 focus:border-indigo-400 outline-none py-1 bg-transparent transition-colors"
              />
            </div>
            <div>
              <label className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider block mb-1">
                Delivery
              </label>
              <input
                type="text"
                value={documentData.foot1 || ""}
                onChange={(e) =>
                  setDocumentData((prev) => ({
                    ...prev,
                    foot1: e.target.value,
                  }))
                }
                className="w-full text-xs border-0 border-b border-slate-200 focus:border-indigo-400 outline-none py-1 bg-transparent transition-colors"
              />
            </div>
            <div>
              <label className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider block mb-1">
                C&F Agent
              </label>
              <input
                type="text"
                value={documentData.notify || ""}
                onChange={(e) =>
                  setDocumentData((prev) => ({
                    ...prev,
                    notify: e.target.value,
                  }))
                }
                className="w-full text-xs border-0 border-b border-slate-200 focus:border-indigo-400 outline-none py-1 bg-transparent transition-colors"
              />
            </div>
            <div>
              <label className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider block mb-1">
                Ship. Ref
              </label>
              <input
                type="text"
                value={documentData.head1 || ""}
                onChange={(e) =>
                  setDocumentData((prev) => ({
                    ...prev,
                    head1: e.target.value,
                  }))
                }
                className="w-full text-xs border-0 border-b border-slate-200 focus:border-indigo-400 outline-none py-1 bg-transparent transition-colors"
              />
            </div>
            <div>
              <label className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider block mb-1">
                LC No.
              </label>
              <input
                type="text"
                value={documentData.lpo_no || ""}
                onChange={(e) =>
                  setDocumentData((prev) => ({
                    ...prev,
                    lpo_no: e.target.value,
                  }))
                }
                className="w-full text-xs border-0 border-b border-slate-200 focus:border-indigo-400 outline-none py-1 bg-transparent transition-colors"
              />
            </div>
            <div>
              <label className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider block mb-1">
                Arrival Date
              </label>
              <input
                type="date"
                value={documentData.due_date || ""}
                onChange={(e) =>
                  setDocumentData((prev) => ({
                    ...prev,
                    due_date: e.target.value,
                  }))
                }
                className="w-full text-xs font-semibold border-0 border-b border-slate-200 outline-none py-1 bg-transparent text-blue-600"
              />
            </div>
          </div>
        </div>

        {/* Financials — compact Net + BC cards */}
        <div className="col-span-3 bg-white rounded-2xl p-4 border border-slate-200/80 shadow-sm flex flex-col gap-3">
          {/* <div className="flex items-center gap-2">
            <div className="w-5 h-5 rounded-md bg-slate-100 flex items-center justify-center">
              <DollarSign size={11} className="text-slate-500" />
            </div>
            <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
              Financials
            </span>
          </div> */}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider block mb-1">
                Currency
              </label>
              <div className="relative">
                <select
                  className="w-full text-xs font-bold border-0 border-b border-slate-200 outline-none py-1 bg-transparent appearance-none cursor-pointer"
                  value={documentData.fc || "AED"}
                  onChange={(e) => {
                    const selectedCur = currencies.find(
                      (c) => c.curno === e.target.value,
                    );
                    setDocumentData((prev) => ({
                      ...prev,
                      fc: e.target.value,
                      po_fcrate: selectedCur
                        ? selectedCur.rate
                        : prev.po_fcrate,
                    }));
                  }}
                >
                  {currencies.map((cur) => (
                    <option key={cur.curno} value={cur.curno}>
                      {cur.curno}
                    </option>
                  ))}
                </select>
                <ChevronDown
                  size={10}
                  className="absolute right-0 top-2 text-slate-400 pointer-events-none"
                />
              </div>
            </div>
            <div>
              <label className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider block mb-1">
                Rate
              </label>
              <input
                type="number"
                value={documentData.po_fcrate || 1}
                onChange={(e) =>
                  setDocumentData((prev) => ({
                    ...prev,
                    po_fcrate: parseFloat(e.target.value) || 1,
                  }))
                }
                className="mono w-full text-xs text-right border-0 border-b border-slate-200 outline-none py-1 bg-transparent"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2 mt-1">
            <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-2">
              <p className="text-[8px] font-black text-indigo-400 uppercase tracking-tighter">
                Net Amount
              </p>
              <p className="text-sm font-black text-indigo-700 leading-none">
                {netAmount.toLocaleString()}
              </p>
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-2 text-right">
              <p className="text-[8px] font-black text-slate-400 uppercase tracking-tighter">
                BC Amount
              </p>
              <p className="text-sm font-black text-slate-600 leading-none">
                {fmt(netAmount * (documentData.po_fcrate || 1))}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ── LINE ITEMS TABLE ── */}
      <div className="bg-white rounded-2xl border border-slate-200/80 shadow-sm overflow-hidden">
        {/* Toolbar */}
        <div className="px-5 py-3 border-b border-slate-100 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div>
              <h2 className="text-sm font-bold text-slate-800 leading-none">
                Line Items
              </h2>
              <p className="text-[10px] text-slate-400 mt-0.5">
                {items.length} items ·{" "}
                {[...new Set(items.map((i) => i.poNo))].length} purchase orders
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-0.5">
            <button
              onClick={() => setFilterActive(false)}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-bold uppercase rounded-md transition-all ${!filterActive ? "bg-white shadow-sm text-indigo-600" : "text-slate-400 hover:text-slate-600"}`}
            >
              <Hash size={10} /> All ({items.length})
            </button>
            <button
              onClick={() => setFilterActive(true)}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-bold uppercase rounded-md transition-all ${filterActive ? "bg-white shadow-sm text-indigo-600" : "text-slate-400 hover:text-slate-600"}`}
            >
              <PackageCheck size={10} /> Shipped ({activeItems.length})
            </button>
          </div>
        </div>
        <div className="overflow-x-auto">
          {/* Column headers */}
          <div
            className="grid bg-slate-50 border-b border-slate-100 px-3"
            style={{
              gridTemplateColumns: tableGrid,
            }}
          >
            {[
              { label: "#", align: "text-center" },
              { label: "PO Ref", align: "text-left", icon: <Tag size={9} /> },
              { label: "Item Details", align: "text-left" },
              { label: "Loc", align: "text-center", icon: <MapPin size={9} /> },
              { label: "Unit", align: "text-center" },
              { label: "PO No", align: "text-right" }, // NEW
              { label: "Ord Qty", align: "text-right" }, // NEW
              { label: "Rcvd Qty", align: "text-right" }, // NEW
              { label: "Shipped Qty", align: "text-right", highlight: true },
              { label: "Unit Cost", align: "text-right" },
              { label: "Line Total", align: "text-right" },
            ].map(({ label, align, highlight, icon }) => (
              <div key={label} className={`py-2.5 px-2 ${align}`}>
                <span
                  className={`inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-widest ${highlight ? "text-indigo-500" : "text-slate-400"}`}
                >
                  {icon}
                  {label}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Rows */}
        <div className="divide-y divide-slate-50">
          {displayedItems.map((item, idx) => {
            const isActive = item.shipQty > 0;
            const lineTotal = item.shipQty * item.cost;
            return (
              <div
                key={item.id}
                className={`row-hover grid px-3 items-center transition-all duration-150 ${isActive ? "bg-emerald-50/30" : "bg-white"}`}
                style={{
                  gridTemplateColumns: tableGrid,
                }}
              >
                <div className="py-3.5 px-2 text-center">
                  <div
                    className={`w-5 h-5 rounded-full flex items-center justify-center mx-auto ${isActive ? "bg-emerald-100" : "bg-slate-100"}`}
                  >
                    {isActive ? (
                      <CheckCircle2 size={12} className="text-emerald-500" />
                    ) : (
                      <span className="mono text-[9px] text-slate-400 font-bold">
                        {idx + 1}
                      </span>
                    )}
                  </div>
                </div>

                <div className="py-3.5 px-2">
                  <span className="mono inline-block text-[10px] font-semibold text-slate-600 bg-slate-100 px-2 py-0.5 rounded-md border border-slate-200/80">
                    {item.poNo}
                  </span>
                </div>

                <div className="py-3.5 px-2">
                  <p className="mono text-xs font-semibold text-slate-700 leading-tight">
                    {item.code}
                  </p>
                  <p className="text-[10px] text-slate-400 font-medium mt-0.5">
                    {item.desc}
                  </p>
                </div>

                <div className="py-3.5 px-2 text-center">
                  <input
                    type="text"
                    value={item.loc || ""}
                    onChange={(e) => {
                      const val = e.target.value;
                      setItems((prev) =>
                        prev.map((it) =>
                          it.id === item.id ? { ...it, loc: val } : it,
                        ),
                      );
                    }}
                    className="w-8 text-center text-xs font-bold border border-transparent hover:border-slate-200 focus:border-indigo-400 focus:bg-indigo-50 rounded-lg outline-none py-0.5 transition-all"
                  />
                </div>

                <div className="py-3.5 px-2 text-center">
                  <span className="text-[10px] font-bold text-slate-400 bg-slate-50 border border-slate-100 px-1.5 py-0.5 rounded">
                    {item.unit}
                  </span>
                </div>
                {/* PO No */}
                <div className="py-3.5 px-2 text-right">
                  <span className="mono text-xs text-slate-400">
                    {item.poNo}
                  </span>
                </div>

                {/* Ord Qty */}
                <div className="py-3.5 px-2 text-right">
                  <span className="mono text-xs text-slate-400">
                    {item.pendQty.toLocaleString()}
                  </span>
                </div>
                {/* Rcvd Qty */}
                <div className="py-3.5 px-2 text-right">
                  <span className="mono text-xs text-slate-400">
                    {/* {item.ordQty.toLocaleString()} */}
                  </span>
                </div>
                {/* Ship Qty */}
                <div className="py-3.5 px-2 flex justify-end">
                  <div
                    className={`inline-flex items-center rounded-lg border transition-all duration-200 ${isActive ? "border-emerald-300 bg-emerald-50 shadow-sm shadow-emerald-100" : "border-slate-200 bg-slate-50 hover:border-indigo-300 hover:bg-indigo-50/30"}`}
                  >
                    <input
                      type="number"
                      placeholder="0"
                      className={`mono w-20 text-right text-sm font-bold bg-transparent outline-none py-1.5 px-2 transition-all ${isActive ? "text-emerald-700" : "text-slate-400 placeholder-slate-300"}`}
                      value={item.shipQty || ""}
                      onChange={(e) => handleQtyChange(item.id, e.target.value)}
                    />
                  </div>
                </div>

                <div className="py-3.5 px-2 text-right">
                  <input
                    type="number"
                    value={item.cost || 0}
                    onChange={(e) => {
                      const val = parseFloat(e.target.value) || 0;
                      setItems((prev) =>
                        prev.map((it) =>
                          it.id === item.id ? { ...it, cost: val } : it,
                        ),
                      );
                    }}
                    className="mono w-20 text-right text-xs text-slate-500 bg-transparent outline-none border-0 border-b border-transparent hover:border-slate-200 focus:border-indigo-300 transition-colors py-1"
                  />
                </div>

                <div className="py-3.5 px-2 text-right">
                  {isActive ? (
                    <span className="mono text-sm font-bold text-slate-900">
                      {fmt(lineTotal)}
                    </span>
                  ) : (
                    <span className="mono text-xs text-slate-300">—</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {displayedItems.length === 0 && (
          <div className="py-20 flex flex-col items-center justify-center gap-3">
            <div className="w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center">
              <PackageSearch size={24} className="text-slate-300" />
            </div>
            <p className="text-sm font-semibold text-slate-400">
              No items in this view
            </p>
            <p className="text-xs text-slate-300 -mt-2">
              Enter shipping quantities to mark items as shipped
            </p>
          </div>
        )}

        {/* Subtotals */}
        {activeItems.length > 0 && (
          <div
            className="grid bg-slate-50 border-t border-slate-200 px-3 py-3 items-center"
            style={{
              gridTemplateColumns: tableGrid,
            }}
          >
            <div />
            <div />
            <div className="px-2">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                {activeItems.length} item(s) with quantity
              </span>
            </div>
            <div />
            <div />
            <div className="text-right px-2">
              <span className="mono text-xs font-bold text-slate-600">
                {totalQty.toFixed(2)}
              </span>
            </div>
            <div />
            <div />
            <div className="text-right px-2">
              <span className="mono text-sm font-bold text-indigo-600">
                {fmt(netAmount)}
              </span>
            </div>
          </div>
        )}
      </div>

      <Modal
        isOpen={modalState.isOpen}
        onClose={closeModal}
        size="xxl"
        header={true}
        title={
          modalState.type === "cr-list"
            ? "Choose a supplier"
            : "Choose purchase order to import"
        }
      >
        {modalState.type === "cr-list" && (
          <CreditCustomerList
            defaultType="Suppliers"
            handleCustomerSelect={(newValue) => {
              console.log("Supplier data new Value", newValue);
              setSupplier(newValue);
              // setSupplierData(newValue);
              console.log("Selected Supplier", newValue);
              closeModal();
            }}
          />
        )}
        {modalState.type === "po-import" && (
          // <TransferInvoiceListModal
          //   setIsModalOpen={setPoImportModalOpen}
          //   initialSearchQuery={supplier?.ACCOUNT || supplier?.code || ""}
          // />
          <TransferOrderModal
            handleSelectAll={handleSelectAll}
            handleSelectOrder={handleSelectOrder}
            orders={modalData}
            loading={loadingModalData}
            selectedOrders={selectedOrders}
            handleTransfer={handleTransfer}
            selectedDocType={"PORD"}
            searchTerm={modalSearchTerm}
            setSearchTerm={setModalSearchTerm}
            fetchModalData={fetchModalData}
            loadingModalData={loadingModalData}
            setSelectedOrders={setSelectedOrders}
            getOrderId={getOrderId}
          />
        )}
      </Modal>
    </div>
  );
};

export default GitHome;
